'use client';

// prettier-ignore
import type { Form, FormFieldBlock } from '@payloadcms/plugin-form-builder/types';
import type React from 'react';
// prettier-ignore
import type { Control, FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';
import type { FormBlock } from '@/payload-types';

import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';

import { useLocale, useTranslations } from 'next-intl';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

import { fields } from './fields';
import { initialFormState } from './state';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RichText } from '@/payload/richtext-editor';

export type Value = unknown;

export interface DataProperty {
  [key: string]: Value;
}

export interface Error {
  message: string;
  status?: string;
}

export interface Data {
  [key: string]: DataProperty | DataProperty[];
}

type FieldComponent = React.FC<
  {
    register: UseFormRegister<FieldValues>;
    control: Control<FieldValues>;
    errors: FieldErrors<FieldValues>;
    form: Form;
  } & FormFieldBlock
>;

type Fields = {
  [K in keyof typeof fields]: FieldComponent;
};

export const FormBlockComponent: React.FC<FormBlock> = ({ form: formData, introContent, enableIntro }) => {
  const form = formData as Form;
  const locale = useLocale();
  const t = useTranslations('forms');

  const defaultValues = initialFormState(form.fields || []);
  const formMethods = useForm<FieldValues>({
    defaultValues,
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    control,
    reset,
    setFocus,
  } = formMethods;

  const [hasSubmitted, setHasSubmitted] = useState<boolean>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const router = useRouter();

  const onSubmit = useCallback(
    (data: Data) => {
      let loadingTimerID: ReturnType<typeof setTimeout>;

      const submitForm = async () => {
        setError(undefined);

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }));

        loadingTimerID = setTimeout(() => {
          setIsLoading(true);
        }, 1000);

        try {
          const req = await fetch(`/api/form-submissions?locale=${locale}`, {
            body: JSON.stringify({
              form: form.id,
              submissionData: dataToSend,
            }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });

          const res = await req.json();

          clearTimeout(loadingTimerID);

          if (req.status >= 400) {
            setIsLoading(false);
            setError({
              message: res.errors?.[0]?.message || t('unexpectedError'),
              status: res.status,
            });
            return;
          }

          setIsLoading(false);
          setHasSubmitted(true);
          reset(defaultValues);

          if (form.confirmationType === 'redirect' && form.redirect) {
            const { url } = form.redirect;
            if (url) {
              try {
                if (url.startsWith('/')) {
                  router.push(url);
                } else {
                  const redirectUrl = new URL(url, window.location.origin);
                  const currentOrigin = window.location.origin;

                  if (redirectUrl.origin === currentOrigin) {
                    router.push(url);
                  } else {
                    console.error('Invalid redirect URL - external redirects not allowed:', url);
                  }
                }
              } catch (e) {
                console.error('Invalid redirect URL:', e);
              }
            }
          }
        } catch (err) {
          setIsLoading(false);
          setError({
            message: t('unexpectedError'),
          });
          console.warn(err);
        }
      };

      void submitForm();
    },
    [form.confirmationType, form.redirect, form.id, defaultValues, locale, router, reset, t],
  );

  const confirmationMessage = form.confirmationType === 'message';

  return (
    <>
      {enableIntro && introContent && <RichText data={introContent} />}

      <FormProvider {...formMethods}>
        {!isLoading && hasSubmitted && confirmationMessage && (
          <Alert
            className="fixed inset-x-4 top-4 z-50 mx-auto w-auto max-w-lg bg-green-50 px-4"
            onClick={() => setHasSubmitted(false)}
            variant="default"
          >
            <CheckCircle2 className="h-4 w-4 text-black" />
            <AlertTitle className="hidden" />
            <AlertDescription className="text-black/80">
              <RichText className="p-0" data={form.confirmationMessage} />
            </AlertDescription>
          </Alert>
        )}

        {isLoading && !hasSubmitted && (
          <Alert className="container mx-4 border-muted-foreground/20 bg-muted/50 px-4" variant="default">
            <Loader2 className="h-4 w-4 animate-spin text-black" />
            <AlertTitle className="font-medium text-black">{t('sending')}</AlertTitle>
            <AlertDescription className="text-black/80">{t('pleaseWait')}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="container mx-4 border-destructive/20 bg-destructive/10 px-4" variant="destructive">
            <XCircle className="h-4 w-4 text-black" />
            <AlertTitle className="font-medium text-black">{t('error')}</AlertTitle>
            <AlertDescription className="text-black/80">{error?.message || t('unexpectedError')}</AlertDescription>
          </Alert>
        )}

        <form
          id={form.id}
          noValidate
          onSubmit={handleSubmit(onSubmit, (invalidFields) => {
            setHasSubmitted(false);
            const firstInvalidField = Object.keys(invalidFields)[0];
            if (firstInvalidField) setFocus(firstInvalidField);
          })}
        >
          <div className="space-y-4">
            {form.fields?.map((field, index) => {
              const blockType = field.blockType as keyof typeof fields;

              if (!fields[blockType]) {
                console.error(`Unsupported block type: ${field.blockType}`);
                return null;
              }

              const Field = fields[blockType] as Fields[typeof blockType];

              return (
                <div key={field.blockName ?? index}>
                  <Field control={control} errors={errors} form={form} register={register} {...field} />
                </div>
              );
            })}

            <Button form={form.id} type="submit" variant="default">
              {form.submitButtonLabel}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
